import { ToastProgrammatic as Toast } from 'buefy';
import mixpanel from 'mixpanel-browser';
import moment from 'moment';

import api from '../../../lib/api';

const mixToken = process.env.VUE_APP_MIXPANEL_TOKEN;
mixpanel.init(mixToken);

function setUserDataInFreshChat(session) {
  const [firstName, ...lastName] = session.name.split(' ');

  if (window.fcWidget) {
    window.fcWidget.user.setProperties({
      firstName,
      lastName: lastName.join(' '),
      email: session.email,
      phone: session.phoneNumber,
      externalId: session.id,
    });
  }
}

export default {
  async signUp({ state, commit }) {
    const { email, password, name } = state;

    const payload = await api.auth.signUp({
      email,
      password,
      name,
    });

    commit('setValues', {
      ...payload,
      signed: true,
    });

    sessionStorage.setItem('access_token', payload.token);

    setUserDataInFreshChat(payload);

    return payload;
  },

  async addRFC({ dispatch }, { rfc, name }) {
    const data = await api.auth.addRFC(rfc, name);

    await dispatch('initAuthentication');

    return data;
  },

  async signUpWithCompany({ state, commit, rootState }) {
    const {
      email,
      name,
      phoneNumber,
      password,
      referencedCode,
    } = state;

    const {
      rfc,
      tradeName,
      name: companyName,
      utmParams,
    } = rootState.company;

    const body = {
      email,
      name: name || companyName,
      rfc,
      tradeName: tradeName || companyName,
      companyName,
      phoneNumber,
      password,
      referencedCode,
      utmParams,
    };

    const { company, ...payload } = await api.auth.signUpWithCompany(body);

    commit('setValues', {
      ...payload,
      signed: true,
      companyId: company.id,
    });

    commit('company/setValues', company, { root: true });

    sessionStorage.setItem('access_token', payload.token);

    setUserDataInFreshChat(payload);

    return payload;
  },

  async passwordRecovery({ state, commit }, token) {
    const { password } = state;

    const { company, ...payload } = await api.auth.passwordRecovery(password, token);

    if (state.rememberMe) localStorage.setItem('access_token', payload.token);
    else sessionStorage.setItem('access_token', payload.token);

    if (company.id !== 0) {
      commit(
        'company/setValues',
        company,
        { root: true },
      );

      commit(
        'invoice/setValues',
        {
          emitterRfc: company.rfc,
          emitterName: company.name,
          emitterRegime: company.regime,
          voucherExpeditionPlace: company.zipCode,
        },
        { root: true },
      );
    }

    commit('setValues', {
      ...payload,
      companyId: company.id,
      signed: true,
    });

    commit('cleanSensitiveData');

    setUserDataInFreshChat(payload);

    return payload;
  },
  async verifyPasswordRecovery({ commit }, token) {
    const payload = await api.auth.verifyPasswordRecovery(token);

    commit('setValues', payload);

    return payload;
  },
  async signIn({ state, commit, dispatch }) {
    const { email, password } = state;

    const { company, ...payload } = await api.auth.signIn(email, password);
    const { packages, subscriberId } = payload;

    mixpanel.identify(subscriberId || company.rfc);

    mixpanel.people.set({
      USER_ID: subscriberId || company.rfc,
      $email: payload.email,
      $name: payload.name,
      company: company.name,
      packages,
      rfc: company.rfc,
      last_login: moment().toDate(),
    });

    mixpanel.track('login', {
      distinct_id: subscriberId || company.rfc,
    });

    if (state.rememberMe) {
      localStorage.setItem('access_token', payload.token);
    } else {
      sessionStorage.setItem('access_token', payload.token);
    }

    if (company.id !== 0) {
      commit(
        'company/setValues',
        company,
        { root: true },
      );

      commit(
        'invoice/setValues',
        {
          emitterRfc: company.rfc,
          emitterName: company.name,
          emitterRegime: company.regime,
          voucherExpeditionPlace: company.zipCode,
        },
        { root: true },
      );

      await dispatch('company/getCompanyById', company.id, { root: true });
    }

    commit('setValues', {
      ...payload,
      companyId: company.id,
      signed: true,
    });

    if (window.fcWidget) {
      window.fcWidget.setExternalId(payload.id);
      window.fcWidget.user.setFirstName(payload.name);
      window.fcWidget.user.setEmail(payload.email);
    }

    commit('cleanSensitiveData');

    setUserDataInFreshChat(payload);

    return payload;
  },

  async initAuthentication({ commit, dispatch }) {
    const { company, ...payload } = await api.auth.validateSession();
    const { packages, subscriberId } = payload;

    mixpanel.identify(subscriberId || company.rfc);
    mixpanel.people.set({
      USER_ID: subscriberId || company.rfc,
      $email: payload.email,
      $name: payload.name,
      company: company.name,
      packages,
      rfc: company.rfc,
      last_login: moment().toDate(),
      created_at: payload.createdAt,
      phone_number: payload.phoneNumber,
    });

    mixpanel.track('login',
      {
        distinct_id: subscriberId || company.rfc,
      });

    if (company.id !== 0) {
      commit(
        'company/setValues',
        company,
        { root: true },
      );

      commit(
        'invoice/setValues',
        {
          emitterRfc: company.rfc,
          emitterName: company.name,
          emitterRegime: company.regime,
          voucherExpeditionPlace: company.zipCode,
        },
        { root: true },
      );

      await dispatch('company/getCompanyById', company.id, { root: true });
    }

    commit('setValues', {
      ...payload,
      companyId: company.id,
      signed: true,
    });

    if (window.fcWidget) {
      window.fcWidget.setExternalId(payload.id);
      window.fcWidget.user.setFirstName(payload.name);
      window.fcWidget.user.setEmail(payload.email);
    }

    setUserDataInFreshChat(payload);

    return payload;
  },

  signOut({ commit }) {
    localStorage.removeItem('access_token');
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('logoutRedirect');

    commit('setValue', { accessor: 'signed', value: false });
  },

  async forgottenPassword({ state }, customEmail) {
    const { email } = state;
    let emailToRecover = email;

    if (customEmail) emailToRecover = customEmail;

    const payload = await api.auth.forgottenPassword(emailToRecover);

    return payload;
  },
  async resendEmailVerification() {
    try {
      await api.auth.resendEmailVerification();

      Toast.open({
        type: 'is-success',
        message: `
          El correo se reenvió correctamente,
          por favor revisa tu bandeja de entrada
        `,
        duration: 2000,
      });
    } catch (error) {
      // eslint-disable-next-line
      console.error(error);

      Toast.open({
        type: 'is-danger',
        message: `
          Ocurrió un error al tratar de reenviar el correo de verificación,
          si el problema persiste contacte a soporte.
        `,
        duration: 2000,
      });
    }
  },
  async verifyEmail({ commit }, token) {
    const { company, ...payload } = await api.auth.verifyEmail(token);

    commit('setValues', {
      ...payload,
      companyId: company.id,
      signed: true,
    });

    commit('company/setValues', company, { root: true });

    sessionStorage.setItem('access_token', payload.token);

    return payload;
  },

  async verifyConfirmationCode({ commit }) {
    const { payload } = await api.auth.verifyConfirmationCode();

    commit('setValues', {
      ...payload,
    });
    return payload;
  },
};
